import AdminPageHeader from "@/components/admin/page-header";
import { useLoadFeedback } from "@/hooks/use-load-feedback";
import { PageState } from "@/components/ui/page-state";
import React, { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import { Plus, Trash2, Download, ExternalLink, Pencil } from "lucide-react";
import api from "@/lib/api";
import { useOutlet } from "@/context/OutletContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

// Jenis meja — sinkron dengan `TABLE_TYPES` di backend (qr-backend/server.py)
// dan param `?type=room` yang dibaca OrderPage.jsx untuk mode Room Service.
const TABLE_TYPES = [
  { value: "dinein", label: "Dine-in — Meja Restoran", noun: "Meja", emoji: "🍽️" },
  { value: "room", label: "Room Service — Kamar", noun: "Kamar", emoji: "🛏️" },
];
const typeMeta = (value) => TABLE_TYPES.find((t) => t.value === value) || TABLE_TYPES[0];
const isRoomType = (value) => value === "room";
const emptyForm = { number: "", label: "", type: "dinein" };

export default function AdminTables() {
  const { activeOutlet } = useOutlet();
  const outletId = activeOutlet?.id;
  const { loading, refreshing, hasLoaded, error: loadError, run } = useLoadFeedback(outletId);
  const outletSlug = activeOutlet?.slug;
  const [tables, setTables] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [qrTable, setQrTable] = useState(null);
  const qrRef = useRef(null);

  const load = useCallback(() => run(async () => {
    if (!outletId) return;
    const { data } = await api.get("/tables", { params: { outlet_id: outletId } });
    setTables(data);
  }), [outletId, run]);

  useEffect(() => { load(); }, [load]);

  const origin = window.location.origin;
  // Meja biasa -> /order?outlet=x&tableNumber=n (Dine-in)
  // Kamar     -> /order?outlet=x&tableNumber=n&type=room (langsung buka Room Service)
  const orderUrl = (t) => `${origin}/order?outlet=${outletSlug}&tableNumber=${t.number}${isRoomType(t.type) ? "&type=room" : ""}`;

  const openAdd = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (t) => {
    setEditing(t);
    setForm({ number: String(t.number ?? ""), label: t.label || "", type: isRoomType(t.type) ? "room" : "dinein" });
    setOpen(true);
  };

  const save = async () => {
    const number = Number(form.number);
    if (!String(form.number).trim() || Number.isNaN(number)) {
      return toast.error(form.type === "room" ? "Nomor kamar wajib diisi" : "Nomor meja wajib diisi");
    }
    if (!Number.isInteger(number) || number < 0) {
      return toast.error(
        form.type === "room"
          ? "Nomor kamar harus angka bulat (0 = QR umum, tamu mengisi sendiri)"
          : "Nomor meja harus angka bulat positif"
      );
    }
    const payload = { outlet_id: outletId, number, label: form.label, type: form.type };
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/tables/${editing.id}`, payload);
        toast.success(`${typeMeta(form.type).noun} ${number} diperbarui, QR ikut berubah`);
      } else {
        await api.post("/tables", payload);
        toast.success(`${typeMeta(form.type).noun} ${number} ditambahkan`);
      }
      setOpen(false); setEditing(null); setForm(emptyForm); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus meja/kamar ini? QR yang sudah tercetak tidak akan berfungsi lagi.")) return;
    try {
      await api.delete(`/tables/${id}`);
      toast.success("Terhapus"); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menghapus");
    }
  };

  const downloadQR = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `qr-${outletSlug}-${isRoomType(qrTable.type) ? "kamar" : "meja"}-${qrTable.number}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  if (loading || (loadError && !hasLoaded)) return <PageState kind={loading ? "loading" : "error"} title={loading ? "Memuat data" : "Data belum tersedia"} description={loadError} onRetry={loadError ? load : undefined} />;

  return (
    <div aria-busy={refreshing} className="admin-page space-y-6" data-testid="admin-tables-page">
      {loadError && <PageState kind="error" title="Data belum dapat diperbarui" description={loadError} onRetry={load} />}
      <AdminPageHeader title="Meja, Kamar & QR Code" description={<> Kelola meja dine-in &amp; kamar room service untuk outlet <b>{activeOutlet?.name || "-"}</b> </>} actions={<Button onClick={openAdd} className="rounded-full" data-testid="add-table-btn">
          <Plus className="w-4 h-4 mr-1" /> <span className="hidden sm:inline">Tambah Meja / Kamar</span><span className="sm:hidden">Tambah</span>
        </Button>} />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {tables.length === 0 && <p className="text-muted-foreground col-span-full">Belum ada meja di outlet ini.</p>}
        {tables.map((t) => {
          const meta = typeMeta(t.type);
          const room = isRoomType(t.type);
          return (
          <div key={t.id} className="surface-card p-4 flex flex-col items-center" data-testid={`table-card-${t.id}`}>
            <div className="bg-white p-2 rounded-lg border border-border">
              <QRCodeCanvas value={orderUrl(t)} size={140} bgColor="#FFFFFF" fgColor="#2C2420"/>
            </div>
            <p className="font-display font-bold text-xl mt-3">{meta.noun} {t.number}</p>
            <span className={`mt-1 inline-flex items-center gap-1 text-[11px] font-medium px-2.5 py-0.5 rounded-full ${room ? "bg-primary/10 text-primary" : "bg-gray-100 text-gray-600"}`} data-testid={`table-type-badge-${t.id}`}>
              {meta.emoji} {room ? "Room Service" : "Dine-in"}
            </span>
            {t.label && <p className="text-xs text-muted-foreground mt-1 text-center">{t.label}</p>}
            <div className="flex gap-1 mt-3 w-full">
              <Button variant="outline" size="sm" className="flex-1 rounded-lg" aria-label={`Lihat QR ${t.number}`} onClick={() => setQrTable(t)} data-testid={`view-qr-${t.id}-btn`}>
                <Download className="w-3.5 h-3.5" />
              </Button>
              <Button asChild variant="outline" size="sm" className="flex-1 rounded-lg">
                <a aria-label={`Buka menu ${t.number}`} href={orderUrl(t)} target="_blank" rel="noreferrer"><ExternalLink className="w-3.5 h-3.5"/></a>
              </Button>
              <Button variant="outline" size="sm" className="flex-1 rounded-lg" aria-label={`Edit meja atau kamar ${t.number}`} onClick={() => openEdit(t)} data-testid={`edit-table-${t.id}-btn`}>
                <Pencil className="w-3.5 h-3.5" />
              </Button>
              <Button variant="outline" size="sm" className="rounded-lg" aria-label={`Hapus meja atau kamar ${t.number}`} onClick={() => remove(t.id)} data-testid={`delete-table-${t.id}-btn`}>
                <Trash2 className="w-3.5 h-3.5 text-destructive"/>
              </Button>
            </div>
          </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
        <DialogContent className="admin-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? `Ubah ${typeMeta(form.type).noun} ${editing.number}` : "Tambah Meja / Kamar"}</DialogTitle>
            <DialogDescription>
              {editing
                ? `Perbarui data untuk outlet ${activeOutlet?.name}. QR code otomatis mengikuti perubahan.`
                : `Buat meja atau kamar baru di outlet ${activeOutlet?.name}. QR code akan otomatis dibuat.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="admintables-select-3">Jenis</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger id="admintables-select-3" className="bg-white mt-1" data-testid="table-type-select"><SelectValue placeholder="Pilih jenis" /></SelectTrigger>
                <SelectContent className="admin-popup">
                  {TABLE_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.emoji} {t.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1" data-testid="table-type-hint">
                {isRoomType(form.type)
                  ? "QR membuka halaman order dalam mode 🛏️ Room Service dengan nomor kamar terisi."
                  : "QR membuka halaman order dalam mode 🍽️ Dine-in dengan nomor meja terkunci."}
              </p>
            </div>
            <div>
              <Label htmlFor="admintables-field-1">{isRoomType(form.type) ? "Nomor Kamar" : "Nomor Meja"} <span className="text-red-500">*</span></Label>
              <Input id="admintables-field-1" type="number" min="0" step="1" placeholder={isRoomType(form.type) ? "Contoh: 1205" : "Contoh: 12"} value={form.number} onChange={(e)=>setForm({...form,number:e.target.value})} className="bg-white mt-1" data-testid="table-number-input"/>
              {isRoomType(form.type) && (
                <p className="text-[11px] text-muted-foreground mt-1">
                  Isi <b>0</b> bila QR ini dipakai bersama (umum) — tamu wajib mengetik nomor kamarnya sendiri di keranjang.
                </p>
              )}
            </div>
            <div><Label htmlFor="admintables-field-2">Label (opsional)</Label><Input id="admintables-field-2" value={form.label} onChange={(e)=>setForm({...form,label:e.target.value})} placeholder={isRoomType(form.type) ? "Contoh: Villa Blok A" : "Contoh: dekat jendela"} className="bg-white"/></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setOpen(false); setEditing(null); }}>Batal</Button>
            <Button onClick={save} disabled={saving} data-testid="save-table-btn">{saving ? "Menyimpan..." : "Simpan"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!qrTable} onOpenChange={(v) => !v && setQrTable(null)}>
        <DialogContent className="admin-dialog">
          <DialogHeader><DialogTitle>QR Code — {activeOutlet?.name} — {typeMeta(qrTable?.type).noun} {qrTable?.number}</DialogTitle><DialogDescription>Scan atau download QR untuk ditempel di {isRoomType(qrTable?.type) ? "kamar tamu" : "meja pelanggan"}.</DialogDescription></DialogHeader>
          {qrTable && (
            <div ref={qrRef} className="flex flex-col items-center gap-3 py-4">
              <div className="p-4 surface-card max-w-full">
                <QRCodeCanvas value={orderUrl(qrTable)} size={280} bgColor="#FFFFFF" fgColor="#2C2420"/>
              </div>
              <span className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full ${isRoomType(qrTable.type) ? "bg-primary/10 text-primary" : "bg-gray-100 text-gray-600"}`}>
                {typeMeta(qrTable.type).emoji} {isRoomType(qrTable.type) ? "Room Service" : "Dine-in"}
              </span>
              <p className="text-xs text-muted-foreground break-all text-center max-w-xs">{orderUrl(qrTable)}</p>
              <Button onClick={downloadQR} className="rounded-full" data-testid="download-qr-btn">
                <Download className="w-4 h-4 mr-1" /> Download PNG
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
